// src/Registration.js
import React, { useState } from "react";
import { 
  Container, 
  Typography, 
  TextField, 
  Button, 
  Alert, 
  Box, 
  Link,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText
} from "@mui/material";

const Registration = ({ onBackToLogin, onRegistrationSuccess }) => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("user"); // Default role is "user"
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setErrorMsg("");
    setSuccessMsg("");
    
    // Validate form fields
    if (!username.trim()) {
      setErrorMsg("Username is required.");
      return;
    }
    
    if (!password.trim()) {
      setErrorMsg("Password is required.");
      return;
    }
    
    if (!email.trim()) {
      setErrorMsg("Email is required.");
      return;
    }
    
    if (password !== confirmPassword) {
      setErrorMsg("Passwords do not match.");
      return;
    }

    setLoading(true);

    // Send registration request to server
    fetch(`${process.env.REACT_APP_API_URL || ''}/api/auth/register`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        // Include auth token if present for admin-created accounts
        ...(localStorage.getItem("token") && {
          "Authorization": `Bearer ${localStorage.getItem("token")}`
        })
      },
      body: JSON.stringify({
        username,
        password,
        role, // Include role field as it's expected by the server
        email 
      }),
    })
      .then(async (res) => {
        console.log(`Registration response status: ${res.status} ${res.statusText}`);
        
        // Clone the response so we can both read it as text and potentially as JSON
        const resClone = res.clone();
        
        // Get the raw response text for logging
        const rawText = await resClone.text();
        console.log("Raw server response:", rawText);
        
        if (!res.ok) {
          // Try to parse the error response as JSON
          try {
            // Try to parse the raw text as JSON
            const errorData = JSON.parse(rawText);
            console.error("Registration error data:", errorData);
            throw new Error(errorData.error || "User creation failed");
          } catch (jsonError) {
            // If we can't parse as JSON, use the raw text or status
            console.error("Error parsing server response:", jsonError);
            const errorMessage = rawText || `Registration failed: ${res.status} ${res.statusText}`;
            throw new Error(errorMessage);
          }
        }
        
        try {
          // Try to parse the successful response as JSON
          return JSON.parse(rawText);
        } catch (jsonError) {
          console.error("Error parsing successful response:", jsonError);
          throw new Error("Registration appeared to succeed but returned invalid data");
        }
      })
      .then((data) => {
        console.log("Registration successful:", data);
        setSuccessMsg("Registration successful! You can now log in.");
        setUsername("");
        setPassword("");
        setConfirmPassword("");
        setEmail("");
        setRole("user");
        
        // If onRegistrationSuccess callback is provided, call it after a delay
        if (onRegistrationSuccess) {
          setTimeout(() => {
            onRegistrationSuccess();
          }, 2000); // Delay to show success message before redirecting
        }
      })
      .catch((error) => {
        setErrorMsg(error.message || "Registration failed. Please try again.");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Container maxWidth="sm" style={{ marginTop: "50px" }}>
      <Typography variant="h4" gutterBottom>
        Create Account
      </Typography>
      {errorMsg && <Alert severity="error" style={{ marginBottom: "10px" }}>{errorMsg}</Alert>}
      {successMsg && <Alert severity="success" style={{ marginBottom: "10px" }}>{successMsg}</Alert>}
      <form onSubmit={handleSubmit}>
        <TextField
          label="Username"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          fullWidth
          margin="normal"
          required
        />
        <TextField
          label="Email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          fullWidth
          margin="normal"
          required
        />
        <TextField
          label="Password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          fullWidth
          margin="normal"
          required
        />
        <TextField
          label="Confirm Password"
          type="password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          fullWidth
          margin="normal"
          required
          error={password !== confirmPassword && confirmPassword !== ""}
          helperText={
            password !== confirmPassword && confirmPassword !== ""
              ? "Passwords do not match"
              : ""
          }
        />
        <FormControl 
          fullWidth 
          margin="normal"
        >
          <InputLabel id="role-select-label">Role</InputLabel>
          <Select
            labelId="role-select-label"
            id="role-select"
            value={role}
            label="Role"
            onChange={(e) => setRole(e.target.value)}
          >
            <MenuItem value="user">User</MenuItem>
            <MenuItem value="admin">Admin</MenuItem>
          </Select>
          <FormHelperText>
            Select the role for this account
          </FormHelperText>
        </FormControl>
        <Button
          type="submit"
          variant="contained"
          color="primary"
          style={{ marginTop: "20px" }}
          disabled={loading}
          fullWidth
        >
          {loading ? "Registering..." : "Register"}
        </Button>
        <Box mt={2} textAlign="center">
          <Link 
            component="button" 
            variant="body2" 
            onClick={onBackToLogin}
          >
            Already have an account? Login
          </Link>
        </Box>
      </form>
    </Container>
  );
};

export default Registration;